import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { directory, isForbidden, listFromFlag, mileage, timesheets } from '@cwos/api-client';
import { FilteredEmpty, Pill, Refused, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { ADMIN_VIEWS_READ, mayListWorkers, usePermissions } from '../permissions.js';
import { ListState } from '../ListStates.js';
import { listPhase, nameResolver } from '../listState.js';
import { useNav } from '../navigation.js';
import { SettingsHeader } from '../settings/common.js';
import { TimeCorrections } from './TimeCorrections.js';
import { TimeOffApprovals } from './TimeOffApprovals.js';
import { TimeExceptions } from './TimeExceptions.js';
import {
  EXCEPTION_COPY,
  TIMESHEET_FILTER_LABELS,
  formatClock,
  formatDay,
  formatDuration,
  formatHours,
  groupWeekByDay,
  describeOpenSessions,
  matchesTimesheetFilter,
  timesheetStatus,
  weekOfInstant,
  weekReconciles,
  type TimesheetFilter,
} from './timeLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Timesheets — the week, its total, and who has to decide about it.
 *
 * The domain is deliberate that recorded time is NOT pay, and this screen keeps
 * that line: it shows hours, exceptions and approval state, and it does not
 * compute overtime, differentials or leave, because Phibian has no payroll
 * domain to compute them against. Inventing an "overtime" column here would be
 * a number nobody could trace.
 */

// ============================================================== THE LIST ====

export function TimesheetList() {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const [filter, setFilter] = useState<TimesheetFilter>('needs_approval');

  const canRead = has('timesheet:read');
  // Scope-aware, because `GET /directory/workers` refuses rather than narrows:
  // both of its gates are `can()` at organization scope, so a `user:read`
  // confined to one program is a 403 and not a shorter roster.
  const canListWorkers = mayListWorkers(permissions);

  const periods = useQuery({
    queryKey: ['timesheets', 'periods'],
    queryFn: () => timesheets.periods(),
    enabled: canRead,
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canRead && canListWorkers,
  });

  // The directory is a secondary read here. Falling back to "A worker you
  // cannot view" for every id made a failed directory fetch look like a wall of
  // permission denials.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);

  if (!canRead) {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">You don’t have access to timesheets</h2>
        <p className="rn-muted">
          Reading timesheets needs the timesheet permission. Ask an administrator if you need it.
        </p>
      </section>
    );
  }

  if (periods.isLoading) return <SkeletonSet surface="table" rows={6} />;
  if (periods.isError && !periods.data) {
    return (
      <LoadFailure
        error={periods.error}
        message="We couldn’t load timesheets just now."
        onRetry={() => void periods.refetch()}
      />
    );
  }

  const all = periods.data ?? [];
  const rows = all.filter((p) => matchesTimesheetFilter(p, filter));
  const awaiting = all.filter((p) => p.status === 'submitted').length;

  return (
    <div className="rn-col">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">
            {awaiting} {awaiting === 1 ? 'timesheet' : 'timesheets'} waiting on approval
          </h2>
          <label className="cmp-filter">
            <span className="rn-muted">Show</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as TimesheetFilter)}
              aria-label="Filter timesheets"
            >
              {(Object.keys(TIMESHEET_FILTER_LABELS) as TimesheetFilter[]).map((key) => (
                <option key={key} value={key}>
                  {TIMESHEET_FILTER_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {all.length === 0 ? (
          <SuccessEmpty
            heading="No timesheets yet"
            message="A timesheet appears once a worker has recorded time in a week."
          />
        ) : rows.length === 0 ? (
          <FilteredEmpty onClear={() => setFilter('all')} />
        ) : (
          <ul className="tc-rows">
            {rows.map((p) => {
              const status = timesheetStatus(p.status);
              return (
                <li key={p.id} className="tc-row">
                  <button
                    type="button"
                    className="tc-row-btn"
                    onClick={() =>
                      navigate(
                        `/time/timesheets/${p.workerId}?weekOf=${weekOfInstant(p.periodStart)}`,
                      )
                    }
                  >
                    <span className="tc-row-main">
                      <span className="tc-row-name">{nameOf(p.workerId)}</span>
                      <span className="rn-muted">
                        {formatDay(p.periodStart)} – {formatDay(p.periodEnd)}
                      </span>
                    </span>
                    <span className="tc-row-side">
                      <Pill variant={status.tone} dot>
                        {status.label}
                      </Pill>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * The statuses a week can be SUBMITTED from, mirroring
 * `apps/api/src/domains/timesheets/defaults.ts` TIMESHEET_TRANSITIONS.submit
 * verbatim: open | returned | reopened. `submitted` and `approved` are absent
 * because they are not "already done, harmless to repeat" — `submitTimesheet`
 * throws TimesheetStateError on a double submit, and an approved week must be
 * reopened by a correction before anything may be sent again.
 *
 * Copied rather than imported because `@cwos/api` is a server package the admin
 * bundle does not depend on. It is small, it is quoted, and a drift between the
 * two shows up as a button that 400s rather than as a silent divergence — which
 * is why the source is named here and not merely paraphrased.
 */
const SUBMITTABLE_FROM = new Set(['open', 'returned', 'reopened']);

/**
 * WHY THERE IS NOTHING TO DECIDE — a sentence about THIS WEEK, per status.
 *
 * The card used to say "this week has not been submitted yet" for every status
 * that was not `submitted`, having reached that branch through `!canApprove`
 * where `canApprove` was `has('timesheet:approve') && status === 'submitted'` —
 * one boolean carrying a question about the READER and a question about the
 * RECORD. Two things were wrong with it and only one of them was the permission
 * half:
 *
 *   * `reopened` IS NOT UNSUBMITTED. A reopened week was submitted, approved,
 *     and then reopened by a later correction — `TIMESHEET_TRANSITIONS.reopen`
 *     accepts only `approved`, so every reopened week has a submission and an
 *     approval behind it. `timesheetStatus` already refuses to call that state
 *     "open" for exactly this reason ("calling it 'open' would hide that an
 *     approval was superseded"), and this card said something stronger and
 *     falser: that the submission never happened. A reviewer reading it would
 *     conclude the week had never been through payroll at all.
 *
 *   * The permission half could not reach the reader — the enclosing branch
 *     charges the SAME key, so inside it `has('timesheet:approve')` is true by
 *     construction and `!canApprove` reduces to `status !== 'submitted'`. It is
 *     latent in the way `AuditLog` and `ComplianceCenter` are latent, and for
 *     the same fragile reason: two keys that happen to be identical. It is
 *     still not the shape to leave in place, so the reader question is now
 *     `authorityGate` and the record question is `awaitingDecision`.
 *
 * `approved` and `returned` are answered by their own branches above this one,
 * so only `open` and `reopened` can reach it. The default is written anyway
 * because a status this screen has never heard of is not a licence to assert a
 * submission history for it.
 */
function nothingToDecideBecause(status: string): string {
  switch (status) {
    case 'open':
      return 'This week has not been submitted yet, so there is nothing to decide.';
    case 'reopened':
      return 'This week was approved and then reopened by a later correction. It has not been submitted again, so there is nothing to decide.';
    default:
      return `This week is not waiting on a decision — it is ${timesheetStatus(status).label.toLowerCase()}.`;
  }
}

// ============================================================ THE DETAIL ====

/**
 * One worker's week.
 *
 * The day rows are computed from the very sessions the week total is made of,
 * and `weekReconciles` checks that they agree. If they ever do not, the screen
 * says so rather than showing a total that contradicts the rows under it — an
 * approval surface that quietly disagrees with its own evidence is worse than
 * one that admits a problem.
 */
export function TimesheetDetail() {
  const navigate = useNav();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { has } = permissions;
  const params = useParams({ strict: false }) as { workerId?: string };
  const workerId = params.workerId ?? '';
  const weekOf = new URLSearchParams(window.location.search).get('weekOf') ?? undefined;

  const [returnReason, setReturnReason] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  /**
   * APPROVE AND RETURN KEEP THEIR OWN ERROR LINES.
   *
   * They shared one `actionError`, rendered at the bottom of the Decision card,
   * so a failed RETURN put a red line under the Approve button and a failed
   * APPROVE put one under the return box. Both controls are on screen at once,
   * which is exactly when a reader has to be told which of the two failed —
   * the same reason the submission card two sections up was given its own.
   */
  const [approveError, setApproveError] = useState<string | null>(null);
  const [returnError, setReturnError] = useState<string | null>(null);
  /**
   * THE OPEN-SESSION 409, AS SOMETHING A PERSON CAN ACT ON.
   *
   * `POST /timesheets/:id/approve` refuses a week that still contains a session
   * with no clock-out: it counts 0 minutes, and approving freezes that
   * understated total into the immutable snapshot payroll pays from. Nothing in
   * this app had ever sent `acknowledgeOpenSessions`, so that refusal was
   * TERMINAL — the week could never be approved, never exported, and the
   * approver was shown the server's own sentence, which names a wire parameter
   * and lists raw uuids at somebody who cannot send either.
   *
   * Non-null means the server refused and named the sessions. It is NOT a
   * client-side prediction of the refusal: this screen does not decide whether
   * a week has open sessions, it renders the answer the server gave to the
   * approval it actually attempted, and the acknowledgement it sends back is
   * re-checked against a freshly derived open set inside the same transaction
   * that writes the snapshot. The dialog is how a person sees what they are
   * accepting; it is not what permits the write.
   */
  const [openSessions, setOpenSessions] = useState<timesheets.OpenSessionConflict | null>(null);
  // Submission keeps its own confirm flag and its own error line rather than
  // sharing the decision card's: the two cards can be on screen at once for a
  // reader who holds both keys, and a failure has to appear under the control
  // that caused it or it reads as a failure of the other one.
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const week = useQuery({
    queryKey: ['timesheets', 'week', workerId, weekOf ?? 'current'],
    queryFn: () => timesheets.workerWeek(workerId, weekOf),
    enabled: workerId !== '' && has('timesheet:read'),
  });
  // A SINGLE record, not the roster, and deliberately still `directory:read`
  // alone. `GET /directory/workers/:id` answers for your own row and for a
  // worker whose `worker.supervisor_id` is you — the same scope
  // `timesheets.workerWeek` above already enforces — so this secondary read
  // succeeds exactly where the primary one does and fails where it fails. It
  // supplies `expectedWeeklyHours` only, and the card below already states the
  // shortfall as unknown rather than as zero when it is absent.
  const worker = useQuery({
    queryKey: ['directory', 'worker', workerId],
    queryFn: () => directory.worker(workerId),
    enabled: workerId !== '' && has('directory:read'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
  };

  /**
   * `acknowledge` is `null` for an ordinary approval and the SESSION IDS the
   * approver was shown for a deliberate one. It is also what tells the two
   * buttons apart while a request is in flight: `approve.isPending` describes
   * the mutation, and this app has already shipped a screen that put
   * "Recording…" on Approve while the operator pressed Deny.
   */
  const approve = useMutation({
    mutationFn: (input: { periodId: string; acknowledge: readonly string[] | null }) =>
      timesheets.approve(
        input.periodId,
        input.acknowledge ? { acknowledgeOpenSessions: input.acknowledge } : {},
      ),
    onMutate: () => setApproveError(null),
    onSuccess: () => {
      setOpenSessions(null);
      invalidate();
    },
    onError: (e: unknown) => {
      // A 409 that names open sessions is not a failure to report, it is a
      // question to ask. Anything else IS a failure, and the conflict panel
      // must come down with it rather than sit under an unrelated error still
      // offering to acknowledge sessions this attempt never got to.
      const conflict = timesheets.openSessionConflict(e);
      if (conflict) {
        setOpenSessions(conflict);
        return;
      }
      setOpenSessions(null);
      setApproveError(e instanceof Error ? e.message : 'We couldn’t approve this week just now.');
    },
  });
  const sendBack = useMutation({
    mutationFn: (input: { periodId: string; reason: string }) =>
      timesheets.returnForCorrection(input.periodId, input.reason),
    onMutate: () => setReturnError(null),
    onSuccess: () => {
      setShowReturn(false);
      setReturnReason('');
      invalidate();
    },
    onError: (e: Error) => setReturnError(e.message),
  });
  /**
   * SUBMIT ON BEHALF OF THE WORKER — the first link of the payroll path, and
   * the one nothing on this side of the wire had ever called.
   *
   * `POST /timesheets/submit` has been routed, guarded and tested since Task 12
   * and `timesheets.submit` has wrapped it in `@cwos/api-client` for as long;
   * `grep -rn "timesheets.submit(" apps/admin/src apps/worker-mobile/src`
   * returned nothing. Nothing downstream of it is reachable without it, and the
   * dead end is structural rather than a matter of degree: `defaults.ts`
   * TIMESHEET_TRANSITIONS makes `approve` legal only from `submitted`, and
   * `listApprovedHoursForExport` reads only `p.status = 'approved'` snapshots.
   * So a pilot organization could record time all week and never produce a
   * payroll export, and the card below this one said so honestly — "this week
   * has not been submitted yet, so there is nothing to decide" — forever.
   *
   * IT IS AN ADMINISTRATIVE ACT, NOT A WORKAROUND, and the route says so in as
   * many words (`timesheets/routes.ts`): "a paper sheet keyed in, a worker who
   * forgot". It is attributed — `submitted_by` is the ACTOR, not the subject —
   * it is audited as `timesheet.submitted`, and it discloses nothing: the reply
   * is the period row, with no aggregate and no snapshot. It advances a
   * workflow state; it does not change or reveal one minute of recorded time.
   *
   * `weekOf` IS SENT AND NEVER OMITTED. The server defaults a missing `weekOf`
   * to `new Date()` and then resolves the week bounds in the ORGANIZATION's
   * zone, so omitting it submits whatever week the server thinks it is now —
   * which is not necessarily the week this screen is showing, because the URL
   * carries `?weekOf=` and a reviewer working Monday morning is routinely
   * looking at the week before. `weekOfInstant(period.periodStart)` is the same
   * conversion the list uses to build that link, so the week that is submitted
   * is the week that was read.
   */
  const submitForWorker = useMutation({
    mutationFn: (input: { workerId: string; weekOf: string }) => timesheets.submit(input),
    onSuccess: () => {
      setShowSubmit(false);
      setSubmitError(null);
      invalidate();
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  if (week.isLoading) return <SkeletonSet surface="drawer" />;
  if (week.isError && !week.data) {
    return (
      <LoadFailure
        error={week.error}
        mainRead
        message="We couldn’t load this timesheet just now."
        onRetry={() => void week.refetch()}
      />
    );
  }
  const data = week.data;
  if (!data) return null;

  const days = groupWeekByDay(data.aggregate);
  // CORE-AUD-216: an organization that has never clocked anyone has no
  // recorded week — "Not measured", never a 0.00h week to submit.
  const notMeasured = data.hasTimeClock === false;
  // Sessions with an UNDECIDED location exception, keyed by session id so the
  // day badge below can never disagree with the panel that lists them —
  // matching on the session, not on a re-derived day string, keeps this
  // immune to timezone drift between the server's calendar and the rows'.
  const locationExceptionSessions = new Set(
    data.exceptions.filter((ex) => ex.flag === 'location_exception').map((ex) => ex.refId),
  );
  const status = timesheetStatus(data.period.status);
  const reconciles = weekReconciles(data.aggregate);
  /**
   * THE TWO QUESTIONS THE DECISION CARD ASKS, KEPT APART.
   *
   * They used to be one `&&` — `has('timesheet:approve') && status ===
   * 'submitted'` — which is how a sentence about the RECORD came to be chosen
   * by a boolean that also carries "the permission set has not arrived". This
   * is only the record half. The reader half is the `has()` on the section
   * below, where it is a HIDDEN CONTROL and nothing is said to anybody.
   *
   * WHY THERE IS NO `authorityGate` + `GateNotice` HERE, which is the shape
   * this repository reaches for and which would be DEAD CODE at this site. The
   * `week` query above is itself `enabled: has('timesheet:read')`, so an
   * unresolved or unreadable permission set leaves `week.data` undefined and
   * this component returns `null` a hundred lines above — the card, the whole
   * screen and any pending notice with it. `unresolved` and `unknown` are
   * therefore unreachable by construction at this point in the render, and a
   * `<GateNotice>` written for them would be a sentence nobody could ever see.
   * It was written, measured against a fixture that holds `/auth/me` in flight,
   * and removed. If the `enabled:` above ever stops gating on a permission,
   * this is the comment that says what to add back.
   */
  const awaitingDecision = data.period.status === 'submitted';
  /**
   * MAY THIS READER SUBMIT THIS WORKER'S WEEK?
   *
   * BOTH KEYS, AND `can()` RATHER THAN `has()`, because that is the pair of
   * questions the route asks. `POST /timesheets/submit` is
   * `guard(request, reply, 'timesheet:submit')` — `can()` at organization scope
   * — and then `requireOwnWorkerOrManage(principal, input.workerId, reply)`,
   * whose first line is `can(principal.authz, 'timesheet:manage')`. A
   * PROGRAM-scoped grant satisfies neither, and `has()` — the flattened
   * "somewhere" question — would read one as organization-wide and put a button
   * here that 403s. That flattening is this repository's recurring defect class
   * (61275c8) and `usePermissions().can` is the mirror written for it.
   *
   * WHY THE OWN-WORKER ARM IS NOT OFFERED HERE, even though the server allows
   * it. `requireOwnWorkerOrManage` also passes a caller whose own worker row IS
   * the subject — that is the worker submitting their own sheet — but this
   * client cannot tell whether that is the case: `GET /auth/me` returns
   * `userId`, `orgId`, roles and grants and NO `workerId` (see `Me` in
   * `packages/api-client/src/auth.ts`), and there is no other read that answers
   * "which worker row am I". Rendering the button on `timesheet:submit` alone
   * would therefore show it to every worker on every colleague's week and 403 on
   * all but one of them. Absent beats refused. The worker's own submission is
   * the worker app's surface, not this one.
   */
  const canSubmitForWorker =
    permissions.can('timesheet:submit') && permissions.can('timesheet:manage');
  const submittable = SUBMITTABLE_FROM.has(data.period.status);
  const expected = worker.data?.expectedWeeklyHours ?? null;

  return (
    <div className="rn-col" style={{ maxWidth: 1000 }}>
      <button
        type="button"
        className="rn-btn-quiet tc-back"
        onClick={() => navigate('/time/timesheets')}
      >
        ← All timesheets
      </button>

      <SettingsHeader
        title={worker.data?.displayName ?? 'Timesheet'}
        blurb={`${formatDay(data.period.periodStart)} – ${formatDay(data.period.periodEnd)}`}
      />

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Recorded time</h2>
          <Pill variant={status.tone} dot>
            {status.label}
          </Pill>
        </div>
        <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
          <div className="db-kpi">
            <span className="db-kpi-label">Total recorded</span>
            <span className="db-kpi-value">
              {notMeasured ? 'Not measured' : formatHours(data.aggregate.totalMinutes)}
            </span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {notMeasured
                  ? 'No time clock connected'
                  : `${data.aggregate.sessionCount} sessions`}
              </span>
            </span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Expected</span>
            <span className="db-kpi-value">{expected !== null ? `${expected}h` : '—'}</span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {expected === null ? 'Not set for this worker' : 'From the worker record'}
              </span>
            </span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Days worked</span>
            <span className="db-kpi-value">{notMeasured ? '—' : String(days.length)}</span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {notMeasured ? 'Not measured' : 'With at least one session'}
              </span>
            </span>
          </div>
        </div>

        {/* Recorded time is not pay. Saying so once, plainly, prevents the
            far worse outcome of someone treating this screen as a paycheck. */}
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          These are recorded hours, not pay. Phibian does not calculate overtime, differentials or
          leave — an approved timesheet is the trusted input a payroll system reads.
        </p>

        {!reconciles ? (
          <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-12)' }}>
            The week total does not match the sum of its sessions. Do not approve this until it is
            investigated.
          </p>
        ) : null}
      </section>

      {data.exceptions.length > 0 ? (
        <section className="rn-card">
          <h2 className="rn-card-title">What is wrong with this week</h2>
          <ul className="cmp-controls">
            {data.exceptions.map((ex) => {
              const copy = EXCEPTION_COPY[ex.flag] ?? {
                label: ex.flag,
                meaning: 'An exception was recorded against this week.',
                tone: 'plain' as const,
              };
              // A session can carry two location exceptions (one per punch), so
              // the key includes the exception's own id when the detail has one.
              const detailId =
                typeof ex.detail?.['exception_id'] === 'string'
                  ? (ex.detail['exception_id'] as string)
                  : '';
              return (
                <li key={`${ex.flag}:${ex.refId}:${detailId}`} className="cmp-control">
                  <div className="cmp-control-head">
                    <span className="cmp-control-title">{copy.label}</span>
                    <Pill variant={copy.tone} dot>
                      Needs review
                    </Pill>
                  </div>
                  <p className="rn-muted cmp-control-desc">{copy.meaning}</p>
                  {ex.flag === 'location_exception' && typeof ex.detail?.['day'] === 'string' ? (
                    <p className="rn-muted" style={{ fontSize: 13 }}>
                      On {formatDay(ex.detail['day'] as string)}
                      {ex.detail['punch_kind'] === 'clock_in'
                        ? ', at clock-in.'
                        : ex.detail['punch_kind'] === 'clock_out'
                          ? ', at clock-out.'
                          : '.'}
                    </p>
                  ) : null}
                  {/* Every exception names the record it refers to, so a
                      reviewer can go and look rather than guess. */}
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    onClick={() => navigate(`/sessions/${ex.refId}`)}
                  >
                    Open the session
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="rn-card">
        <h2 className="rn-card-title">Day by day</h2>
        {notMeasured ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            Not measured — this organization has no time clock connected, so no sessions are
            recorded here. This is not a week with no work.
          </p>
        ) : days.length === 0 ? (
          <SuccessEmpty
            heading="No time recorded this week"
            message="Sessions appear here as the worker clocks in and out."
          />
        ) : (
          <ul className="tc-rows">
            {days.map((day) => (
              <li key={day.dayIso} className="tc-row">
                <div className="tc-day">
                  <div className="tc-day-head">
                    <span className="tc-row-name">{formatDay(day.dayIso)}</span>
                    {day.rows.some((row) => locationExceptionSessions.has(row.sessionId)) ? (
                      <Pill variant="look" dot>
                        Location exception
                      </Pill>
                    ) : null}
                    <span className="tc-elapsed">
                      {formatDuration(day.minutes)}
                      {day.incomplete ? <span className="rn-muted"> (so far)</span> : null}
                    </span>
                  </div>
                  <p className="rn-muted">
                    {formatClock(day.firstIn)} →{' '}
                    {day.lastOut ? formatClock(day.lastOut) : 'still clocked in'}
                    {' · '}
                    {day.rows.length} {day.rows.length === 1 ? 'session' : 'sessions'}
                  </p>
                  <ul className="tc-sessions">
                    {day.rows.map((row) => (
                      <li key={row.sessionId}>
                        <button
                          type="button"
                          className="rn-link"
                          onClick={() => navigate(`/sessions/${row.sessionId}`)}
                        >
                          {formatClock(row.clockedInAt)} →{' '}
                          {row.clockedOutAt ? formatClock(row.clockedOutAt) : 'open'}
                        </button>
                        <span className="rn-muted"> · {formatDuration(row.minutes)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {canSubmitForWorker && submittable && !notMeasured ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Submission</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Nothing can be approved, and no payroll export can include this week, until it has been
            submitted. If {worker.data?.displayName ?? 'this worker'} cannot submit it themselves —
            a paper sheet handed in, a week they forgot — you can send it for approval on their
            behalf.
          </p>
          {/* Attribution is not a detail a reader should have to infer from an
              audit log they may not be able to read. The server records the
              ACTOR in `submitted_by`, not the subject, so saying so here is
              describing what happens rather than reassuring about it. */}
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            It will be recorded as submitted by you, not by them. It does not change any recorded
            hours; it only moves the week into the approval queue.
          </p>
          <div className="tc-drawer-actions">
            <button
              type="button"
              className="rn-btn"
              onClick={() => {
                setShowSubmit((v) => !v);
                setSubmitError(null);
              }}
            >
              {showSubmit ? 'Not now' : 'Submit this week for approval'}
            </button>
          </div>
          {showSubmit ? (
            <div className="tc-return">
              <p className="rn-muted">
                Submit {formatDay(data.period.periodStart)} – {formatDay(data.period.periodEnd)} for
                approval?
              </p>
              <button
                type="button"
                className="rn-btn"
                disabled={submitForWorker.isPending}
                onClick={() =>
                  submitForWorker.mutate({
                    workerId: data.period.workerId,
                    weekOf: weekOfInstant(data.period.periodStart),
                  })
                }
              >
                {submitForWorker.isPending ? 'Submitting…' : 'Yes, submit it'}
              </button>
            </div>
          ) : null}
          {/* A prior review of this app found four mutations with no error
              branch anywhere: the request failed and the screen said nothing,
              so the reader believed the week was submitted when it was not.
              This is the branch. */}
          {submitError ? (
            <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-12)' }}>
              {submitError}
            </p>
          ) : null}
        </section>
      ) : null}

      {has('timesheet:approve') ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Decision</h2>
          {data.period.status === 'approved' ? (
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              Approved{data.period.approvedAt ? ` on ${formatDay(data.period.approvedAt)}` : ''}. A
              later correction to this week will reopen it.
            </p>
          ) : data.period.status === 'returned' ? (
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              Returned for correction: {data.period.returnReason ?? 'no reason recorded'}
            </p>
          ) : !awaitingDecision ? (
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              {nothingToDecideBecause(data.period.status)}
              {canSubmitForWorker && submittable
                ? ' You can send it for approval on the worker\u2019s behalf above.'
                : ''}
            </p>
          ) : (
            <>
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                Approving records what these hours were at this moment. Phibian will not let you
                approve your own timesheet.
              </p>
              <div className="tc-drawer-actions">
                <button
                  type="button"
                  className="rn-btn"
                  disabled={approve.isPending || !reconciles}
                  onClick={() => approve.mutate({ periodId: data.period.id, acknowledge: null })}
                >
                  {approve.isPending && approve.variables?.acknowledge === null
                    ? 'Approving…'
                    : openSessions
                      ? 'Check again and approve'
                      : 'Approve this week'}
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => setShowReturn((v) => !v)}
                >
                  Return for correction
                </button>
              </div>

              {/* WHAT THE SERVER REFUSED, AND WHAT ACCEPTING IT COSTS.
                  Rendered only after an approval was actually attempted and
                  refused — this screen never predicts the refusal, because a
                  client-side guess would be a second, drifting copy of a rule
                  the approval transaction re-derives anyway. */}
              {openSessions ? (
                <OpenSessionConfirm
                  conflict={openSessions}
                  rows={data.aggregate.rows}
                  totalMinutes={data.aggregate.totalMinutes}
                  busy={approve.isPending && approve.variables?.acknowledge !== null}
                  onOpenSession={(sessionId) => navigate(`/sessions/${sessionId}`)}
                  onAccept={() =>
                    approve.mutate({
                      periodId: data.period.id,
                      // EVERY SESSION THE SERVER SAID IS OPEN, which is what
                      // the panel above lists — not just the ones that were
                      // newly unacknowledged. The server's predicate is "every
                      // open session was named", so sending the smaller list
                      // would be refused again for the sessions the approver
                      // had already read and accepted.
                      acknowledge: openSessions.openSessionIds,
                    })
                  }
                  onDismiss={() => setOpenSessions(null)}
                />
              ) : null}

              {/* Approve's own line. See the two useState declarations above:
                  a failed return used to render here, under the Approve
                  button. */}
              {approveError ? (
                <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-12)' }}>
                  {approveError}
                </p>
              ) : null}
              {showReturn ? (
                <div className="tc-return">
                  <label className="cmp-filter">
                    <span className="rn-muted">Why is it going back?</span>
                    <input
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Tuesday's clock-out is missing"
                    />
                  </label>
                  <button
                    type="button"
                    className="rn-btn"
                    disabled={returnReason.trim().length === 0 || sendBack.isPending}
                    onClick={() =>
                      sendBack.mutate({ periodId: data.period.id, reason: returnReason.trim() })
                    }
                  >
                    {sendBack.isPending ? 'Returning…' : 'Send it back'}
                  </button>
                  {returnError ? (
                    <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-12)' }}>
                      {returnError}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {/* WHAT WAS ACTUALLY APPROVED. Only for a week that has been: a period
          with no `approvedAt` has no snapshot, and asking for one would be a
          request whose only honest answer is "nothing yet". */}
      {has('timesheet:read') && data.period.approvedAt ? (
        <ApprovedSnapshot period={data.period} currentMinutes={data.aggregate.totalMinutes} />
      ) : null}
    </div>
  );
}

/**
 * THE FROZEN WEEK — the immutable record payroll is paid from.
 *
 * `GET /timesheets/:id/snapshot` and `timesheets.snapshot()` have both existed
 * since Task 12 and the binding had ZERO call sites in any app, so the one
 * artefact that answers "what did the approver actually sign off" could not be
 * looked at from inside the product. That matters most exactly when the week
 * has moved on: an approved period is REOPENED by any later correction, and
 * from that moment the day-by-day rows above and the figure payroll holds are
 * two different numbers with nothing on screen admitting it.
 *
 * FOUR STATES, NOT TWO. The route REFUSES outright (403) for a caller who may
 * not read this worker's hours — the same `requireHoursReach` the week view
 * takes — and that is not an absent snapshot. A period that was never approved
 * has no snapshot at all. A failed fetch is neither. Only the fourth is a
 * record, and `rows` inside it is read through the withholding contract rather
 * than by testing it for null: `rowsWithheld` is the field that separates "kept
 * from you" from "there were none", and a null-check that ran first would print
 * the absence sentence over a withholding.
 */
function ApprovedSnapshot({
  period,
  currentMinutes,
}: {
  period: timesheets.TimesheetPeriod;
  /** What the week holds NOW, for the comparison a reopen makes necessary. */
  currentMinutes: number;
}) {
  const snapshot = useQuery({
    queryKey: ['timesheets', 'snapshot', period.id],
    queryFn: () => timesheets.snapshot(period.id),
    // A 403 here is an ANSWER about this reader and this worker. Asking again
    // cannot change it, and a retry loop against a refusal is noise in the
    // server's log and a lie on the screen.
    retry: false,
  });

  if (snapshot.isLoading) return <SkeletonSet surface="drawer" />;

  if (snapshot.isError) {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">What was approved</h2>
        {isForbidden(snapshot.error) ? (
          <Refused
            heading="The frozen week is not shown to you"
            message="Reading the per-session record this week was approved on needs reach over this worker's hours. It exists — it is not being shown to you."
          />
        ) : (
          <LoadFailure
            error={snapshot.error}
            message="We couldn’t load the frozen record of this approval. This is not the same as there being none."
            onRetry={() => void snapshot.refetch()}
          />
        )}
      </section>
    );
  }

  const frozen = snapshot.data;
  if (!frozen) {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">What was approved</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          No frozen record was found for this week, even though it has been approved. That is a
          record that should exist — not a week nobody signed off.
        </p>
      </section>
    );
  }

  // NOTATION A/C, FLAG FIRST. On this route the server refuses rather than
  // emptying, so `rowsWithheld` is false today — but `POST /:id/approve`
  // returns the same shape WITH the flag set, and a screen that read
  // `rows == null` as "no sessions" would be wrong the moment those two
  // payloads meet.
  const rows = listFromFlag(
    frozen.rows,
    frozen.rowsWithheld,
    'No sessions were frozen in this week.',
  );
  const drifted = frozen.totalMinutes !== currentMinutes;

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">What was approved</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Frozen on {formatDay(frozen.approvedAt)}. This is the immutable record a payroll export
        reads — it is never recalculated, and a later correction does not change it.
      </p>

      <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
        <div className="db-kpi">
          <span className="db-kpi-label">Hours approved</span>
          <span className="db-kpi-value">{formatHours(frozen.totalMinutes)}</span>
          <span className="db-kpi-foot">
            <span className="rn-muted">{formatDuration(frozen.totalMinutes)}</span>
          </span>
        </div>
        <div className="db-kpi">
          <span className="db-kpi-label">Sessions frozen</span>
          <span className="db-kpi-value">{frozen.sessionCount}</span>
          <span className="db-kpi-foot">
            {/* `sessionCount` is present whether or not `rows` is, which is
                what lets a reader who may not see the breakdown still know
                that a withheld `rows` is not an empty week. */}
            <span className="rn-muted">Counted at approval</span>
          </span>
        </div>
      </div>

      {period.reopenedAt ? (
        // THE SENTENCE THIS PANEL EXISTS FOR. `reopenedReason` was carried in
        // the payload and rendered nowhere, so "this week was reopened" was
        // stated without the correction that reopened it.
        <p className="tc-warn" role="status" style={{ marginTop: 'var(--space-12)' }}>
          This week was reopened on {formatDay(period.reopenedAt)} after it was approved
          {period.reopenedReason
            ? `: ${period.reopenedReason}`
            : ' — no reason was recorded with the reopen'}
          .{' '}
          {drifted
            ? `The week now holds ${formatDuration(currentMinutes)}; the figure above is what was approved, and what an export would still report until it is approved again.`
            : 'The figures have not changed, but the approval no longer stands over them.'}
        </p>
      ) : null}

      {rows.state === 'shown' && rows.value ? (
        <ul className="tc-sessions" style={{ marginTop: 'var(--space-12)' }}>
          {rows.value.map((row) => (
            <li key={row.sessionId}>
              {formatDay(row.clockedInAt)} · {formatClock(row.clockedInAt)} →{' '}
              {row.clockedOutAt ? formatClock(row.clockedOutAt) : 'open at approval'}
              <span className="rn-muted"> · {formatDuration(row.minutes)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          {rows.placeholder}
        </p>
      )}
    </section>
  );
}

/**
 * THE OPEN-SESSION CONFIRM.
 *
 * The 409 this renders is a real refusal from a real attempt, and until this
 * existed it was a DEAD END: `acknowledgeOpenSessions` appeared nowhere in
 * `apps/` or `packages/api-client`, so a week containing a session with no
 * clock-out could never be approved and never be exported — a payroll blocker
 * with no way out of it from inside the product — and the approver was shown
 * the server's own sentence, which names a wire parameter and lists uuids.
 *
 * WHAT THIS IS NOT. It is not a permission, and it is not the thing that lets
 * the write happen. The acknowledgement it sends names sessions, and
 * `approveTimesheet` re-derives the week's open sessions inside the same
 * transaction that writes the snapshot and refuses any it was not told about.
 * If a worker clocks in while this panel is on screen, pressing the button
 * below produces another refusal naming the new session, not an approval that
 * quietly swallowed it. This panel exists so that a person can SEE what they
 * are accepting; the server remains the one deciding whether they may.
 *
 * WHY IT LISTS THEM INSTEAD OF ASKING ONCE. "Approve anyway" as a bare
 * checkbox would be a control whose meaning changes between the moment it is
 * read and the moment it is pressed. Naming the sessions makes the
 * acknowledgement a statement about specific records, which is also what the
 * approval's audit event stores.
 */
function OpenSessionConfirm({
  conflict,
  rows,
  totalMinutes,
  busy,
  onAccept,
  onDismiss,
  onOpenSession,
}: {
  conflict: timesheets.OpenSessionConflict;
  rows: readonly timesheets.AggregateRow[];
  totalMinutes: number;
  /** True only while THIS action is in flight — never a bare `isPending`. */
  busy: boolean;
  onAccept: () => void;
  onDismiss: () => void;
  onOpenSession: (sessionId: string) => void;
}) {
  const subjects = describeOpenSessions(conflict, rows);
  const count = subjects.length;
  // Only meaningful on a SECOND attempt: on the first, everything is
  // unacknowledged and saying so would be noise.
  const arrivedLate = subjects.filter((s) => s.newSinceLastAttempt);
  const isRetry = arrivedLate.length > 0 && arrivedLate.length < count;

  return (
    <div className="tc-return" role="group" aria-labelledby="tc-open-session-heading">
      <h3 id="tc-open-session-heading" className="rn-card-title" style={{ marginTop: 0 }}>
        {count === 1
          ? 'One session in this week is still open'
          : `${count} sessions in this week are still open`}
      </h3>
      <p className="rn-muted">
        A session with no clock-out counts as zero minutes. Approving now freezes the week at{' '}
        {formatDuration(totalMinutes)} — the total below — and that figure is what a payroll export
        reads. It will not be corrected by the clock-out arriving later; the week is reopened
        instead, and somebody has to approve it again.
      </p>
      {isRetry ? (
        // NOT the same sentence as "these are open". A session that opened
        // while this panel was on screen is a fact about the last few seconds,
        // and an approver re-pressing the button deserves to be told the list
        // changed under them rather than shown a longer list with no
        // explanation.
        <p className="tc-warn" role="status">
          {arrivedLate.length === 1
            ? 'One of these opened since you last tried to approve this week.'
            : `${arrivedLate.length} of these opened since you last tried to approve this week.`}
        </p>
      ) : null}
      <ul className="tc-sessions">
        {subjects.map((subject) => (
          <li key={subject.sessionId}>
            <button
              type="button"
              className="rn-link"
              onClick={() => onOpenSession(subject.sessionId)}
            >
              {subject.clockedInAt
                ? `Clocked in ${formatClock(subject.clockedInAt)} on ${formatDay(subject.clockedInAt)}`
                : 'Open the session'}
            </button>
            <span className="rn-muted">
              {' · '}
              {subject.unknownToThisScreen
                ? // NOT dropped, and not described as if it were one of the
                  // rows on screen. The server named it; this screen's copy of
                  // the week predates it. Saying so is the difference between
                  // acknowledging three sessions and being told you
                  // acknowledged two.
                  'recorded after this page loaded — open it to see the times'
                : 'still clocked in · 0 minutes counted'}
            </span>
          </li>
        ))}
      </ul>
      <div className="tc-drawer-actions">
        <button type="button" className="rn-btn" disabled={busy} onClick={onAccept}>
          {busy
            ? 'Approving with the open sessions…'
            : count === 1
              ? 'Approve anyway, accepting this session at zero minutes'
              : `Approve anyway, accepting these ${count} sessions at zero minutes`}
        </button>
        <button type="button" className="rn-btn-quiet" disabled={busy} onClick={onDismiss}>
          Not yet — leave it unapproved
        </button>
      </div>
    </div>
  );
}

// ============================================================= APPROVALS ====

/**
 * The approvals queue.
 *
 * Deliberately cross-domain: an administrator should not have to visit three
 * modules to find out what is waiting on them. Each row links to the surface
 * that owns the decision rather than trying to re-implement it here.
 */
/**
 * THE DECISION QUEUE (Phase E — owner directive): one surface for everything
 * waiting on authorized judgment — timesheet approvals, mileage review, time
 * corrections, time-off requests, and clock exceptions — with the REAL
 * domain sections doing the deciding. Nothing here invents a universal verb:
 * each kind keeps its own actions, permission gates, and consequence
 * language, because the underlying domains differ and pretending otherwise
 * would fake an action the server does not have. The chips are a filter over
 * the same sections, not a new model; "All" is the default and the whole
 * queue. Tasks stay deliberately elsewhere: a task is work to perform, a
 * decision is judgment to exercise.
 */
type DecisionKind = 'all' | 'timesheets' | 'mileage' | 'corrections' | 'timeoff' | 'exceptions';

const DECISION_KINDS: Array<{ key: DecisionKind; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'timesheets', label: 'Timesheets' },
  { key: 'mileage', label: 'Mileage' },
  { key: 'corrections', label: 'Corrections' },
  { key: 'timeoff', label: 'Time off' },
  { key: 'exceptions', label: 'Clock exceptions' },
];

export function Approvals() {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const canListWorkers = mayListWorkers(permissions);
  const [kind, setKind] = useState<DecisionKind>('all');

  const periods = useQuery({
    queryKey: ['timesheets', 'periods'],
    queryFn: () => timesheets.periods(),
    enabled: has('timesheet:read'),
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });

  // The directory is a secondary read here. Falling back to "A worker you
  // cannot view" for every id made a failed directory fetch look like a wall of
  // permission denials.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);

  const submitted = (periods.data ?? []).filter((p) => p.status === 'submitted');

  const show = (k: DecisionKind) => kind === 'all' || kind === k;

  return (
    <div className="rn-col">
      <div className="rn-card-head" style={{ marginBottom: 0 }}>
        <h1 className="rn-card-title">Decisions</h1>
        <div className="rn-tabs" role="group" aria-label="Filter decisions by kind">
          {DECISION_KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className="rn-tab"
              aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {show('timesheets') ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Timesheets waiting on you</h2>
          {/* "Nothing waiting" was rendered from `periods.data ?? []`, so a failed
            read told an approver their queue was clear. The sibling panel below
            (`MileageApprovals`) was the only one on this screen that had ever
            made the distinction; now all three do. */}
          <ListState
            phase={listPhase(periods, submitted.length)}
            rows={5}
            onRetry={() => void periods.refetch()}
            failedMessage="We couldn’t load the approval queue just now, so we cannot say whether anything is waiting."
            refused={
              <Refused message="Your account cannot read timesheets, so approvals are not listed here." />
            }
            empty={
              <SuccessEmpty
                heading="Nothing waiting"
                message="Submitted timesheets appear here for approval."
              />
            }
          >
            <ul className="tc-rows">
              {submitted.map((p) => (
                <li key={p.id} className="tc-row">
                  <button
                    type="button"
                    className="tc-row-btn"
                    onClick={() =>
                      navigate(
                        `/time/timesheets/${p.workerId}?weekOf=${weekOfInstant(p.periodStart)}`,
                      )
                    }
                  >
                    <span className="tc-row-main">
                      <span className="tc-row-name">{nameOf(p.workerId)}</span>
                      <span className="rn-muted">
                        {formatDay(p.periodStart)} – {formatDay(p.periodEnd)}
                        {p.submittedAt ? ` · submitted ${formatDay(p.submittedAt)}` : ''}
                      </span>
                    </span>
                    <span className="tc-row-side">
                      <Pill variant="look" dot>
                        Decide
                      </Pill>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </ListState>
        </section>
      ) : null}

      {show('mileage') ? <MileageApprovals /> : null}
      {/* The correction queue used to be a READ-ONLY list here: it named what
          was waiting and offered no way to decide it, because
          `decideAdjustment` had no caller anywhere in the product. It is now
          the decision surface, plus the form an office user files one from.
          See features/time/TimeCorrections.tsx. */}
      {show('corrections') ? <TimeCorrections /> : null}
      {/* Time off was the fourth thing waiting on this screen's reader and the
          only one with no verb anywhere in the product: `POST
          /timeoff/requests/:id/decision` shipped with Task 13 and had no admin
          caller, so a filed request stayed `pending` for good. See
          features/time/TimeOffApprovals.tsx. */}
      {show('timeoff') ? <TimeOffApprovals /> : null}
      {/* Clock exceptions belong in the same queue (owner ruling). The section
          is the SAME surface `/time/exceptions` serves — one implementation,
          two doors — and it appears only for a principal whose admin-views key
          its reads charge, absent otherwise like the rail.

          `show('exceptions')`, NOT `kind === 'exceptions'`. This was the one
          section of five that tested `kind` directly, so it appeared ONLY on
          its own chip and never on "All" — while this component's own header
          says, four lines up, that "'All' is the default and the whole queue".
          It was not the whole queue: an administrator opening Decisions saw
          four of the five kinds waiting on them and had no way to learn that a
          fifth existed except by pressing a chip. A queue that silently omits a
          kind is worse than one that does not have it, because the reader has
          been told the list is complete.

          THE REFUSAL STAYS ON `kind ===`, deliberately. The section is absent
          on "All" for a principal without the key — "absent otherwise like the
          rail", above — and putting a refusal card into the default view for
          everyone who lacks `admin-views:read` would replace a silent omission
          with a permanent apology. A refusal answers a question somebody asked,
          so it appears when they press the chip. */}
      {show('exceptions') && has(ADMIN_VIEWS_READ) ? <TimeExceptions /> : null}
      {kind === 'exceptions' && !has(ADMIN_VIEWS_READ) ? (
        <Refused message="Clock exceptions read from the workforce board, which your account cannot read." />
      ) : null}
    </div>
  );
}

/** Mileage trips waiting on review — owned by the Mileage area, surfaced here. */
function MileageApprovals() {
  const navigate = useNav();
  const { has } = usePermissions();
  const canReview = has('mileage:review');

  const trips = useQuery({
    queryKey: ['mileage', 'review-count'],
    // `ready_for_review` is the state the mileage domain actually uses for "a
    // person still has to decide about this". It is one of six values the route
    // accepts, and it is NOT the same as `needs_correction`, which is a trip
    // already decided on and sent back.
    //
    // This asked for `needs_review`, which the enum has never contained. The
    // route rejected it as a 400, the query resolved to undefined, the count
    // fell to zero, and the panel told every reviewer "No trips are waiting on a
    // decision" while trips were in fact waiting. A wrong filter value is not a
    // narrower answer here; it is a silent, confident, wrong one.
    queryFn: () => mileage.trips({ reviewState: 'ready_for_review' }),
    enabled: canReview,
  });

  if (!canReview) return null;

  // An ERROR is not zero. Failing to ask the question cannot be reported as
  // "nothing is waiting" — that is the shape of the bug this panel just had.
  const failed = trips.isError;
  const count = trips.data?.trips?.length ?? 0;
  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">Mileage waiting on review</h2>
        <button type="button" className="rn-btn-quiet" onClick={() => navigate('/mileage')}>
          Open mileage
        </button>
      </div>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        {trips.isLoading
          ? 'Checking…'
          : failed
            ? 'Mileage could not be checked just now. Open mileage to see what is waiting.'
            : count === 0
              ? 'No trips are waiting on a decision.'
              : `${count} ${count === 1 ? 'trip needs' : 'trips need'} a decision.`}
      </p>
    </section>
  );
}
